import * as React from 'react';
import type { Editor } from '@tiptap/react';
import { BaselineIcon, CheckIcon, PaletteIcon, XIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { hexToRgb, normalizeTextColour, parseRgbChannel, rgbToHex, TEXT_COLOUR_PALETTE } from './document-text-colour';

const CHANNELS = [
  { key: 'red', label: 'Red', short: 'R' },
  { key: 'green', label: 'Green', short: 'G' },
  { key: 'blue', label: 'Blue', short: 'B' },
] as const;

type ChannelDraft = { colour: string; values: [string, string, string] };

interface FieldProps { id: string; value: string; disabled: boolean; onChange: (colour: string) => void }

/** Text colour editor: colour picker, hex value and a two-way RGB converter that all edit the same `#RRGGBB` value. */
export function TextColourField({ id, value, disabled, onChange }: FieldProps) {
  const rgb = hexToRgb(value);
  const [draft, setDraft] = React.useState<ChannelDraft | null>(null);
  const channels: [string, string, string] = draft && draft.colour === value
    ? draft.values
    : rgb ? [String(rgb[0]), String(rgb[1]), String(rgb[2])] : ['', '', ''];
  const invalidChannel = channels.some(channel => parseRgbChannel(channel) === null);
  const changeChannel = (index: number, next: string) => {
    const values = channels.map((channel, position) => position === index ? next : channel) as [string, string, string];
    const parsed = values.map(parseRgbChannel);
    if (parsed.every((channel): channel is number => channel !== null)) {
      const colour = rgbToHex([parsed[0]!, parsed[1]!, parsed[2]!]);
      setDraft({ colour, values });
      onChange(colour);
    } else {
      setDraft({ colour: value, values });
    }
  };
  return <div className="space-y-2">
    <Label htmlFor={id}>Text colour</Label>
    <div className="flex items-center gap-2">
      <input type="color" aria-label="Choose text colour" value={rgb ? rgbToHex(rgb).toLowerCase() : '#000000'} disabled={disabled}
        className="h-9 w-12 shrink-0 cursor-pointer rounded-md border border-input bg-transparent p-1 disabled:cursor-not-allowed disabled:opacity-50"
        onChange={event => onChange(event.target.value.toUpperCase())} />
      <Input id={id} value={value} maxLength={7} disabled={disabled} placeholder="#000000" onChange={event => onChange(event.target.value)} />
    </div>
    <div role="group" aria-label="RGB converter" className="grid grid-cols-3 gap-2">
      {CHANNELS.map((channel, index) => <div key={channel.key} className="flex items-center gap-1.5">
        <span aria-hidden="true" className="w-3 text-xs text-muted-foreground">{channel.short}</span>
        <Input id={id + '-' + channel.key} aria-label={channel.label} type="number" inputMode="numeric" min={0} max={255} step={1} value={channels[index]} disabled={disabled}
          aria-invalid={parseRgbChannel(channels[index] ?? '') === null} onChange={event => changeChannel(index, event.target.value)} />
      </div>)}
    </div>
    {invalidChannel && !disabled && <p className="text-xs text-destructive">{rgb ? 'Each RGB value must be a whole number from 0 to 255.' : 'Enter a colour as #RRGGBB or as RGB values from 0 to 255.'}</p>}
  </div>;
}

/** Current text colour at the selection, or undefined for the style's automatic colour. */
export function currentTextColour(editor: Editor): string | undefined {
  return normalizeTextColour(editor.getAttributes('textStyle').color) ?? undefined;
}

/** Home → Font formatting text colour: safe palette, custom colour and Automatic (clear). */
export function DocumentTextColourMenu({ editor, disabled }: { editor: Editor; disabled: boolean }) {
  const [custom, setCustom] = React.useState<string>();
  const current = currentTextColour(editor);
  const unavailable = disabled || !editor.isEditable;
  const apply = (colour: string) => { if (!unavailable) editor.chain().focus().setTextColour(colour).run(); };
  const clear = () => { if (!unavailable) editor.chain().focus().unsetTextColour().run(); };
  const customValid = custom !== undefined && normalizeTextColour(custom) !== null && /^#[0-9a-f]{6}$/i.test(custom);
  return <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant={current ? 'secondary' : 'ghost'} size="icon" className="size-9" disabled={unavailable}
          aria-label={current ? 'Text colour, ' + (TEXT_COLOUR_PALETTE.find(entry => entry.value === current)?.name ?? current) : 'Text colour, automatic'} title="Text colour">
          <span className="flex flex-col items-center leading-none"><BaselineIcon aria-hidden className="size-4" />
            <span aria-hidden className="mt-0.5 h-1 w-4 rounded-sm border border-border" style={current ? { backgroundColor: current } : undefined} /></span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>Text colour</DropdownMenuLabel>
        <DropdownMenuItem onSelect={clear}><XIcon aria-hidden />Automatic{!current && <CheckIcon aria-hidden className="ml-auto" />}</DropdownMenuItem>
        <DropdownMenuSeparator />
        {TEXT_COLOUR_PALETTE.map(entry => <DropdownMenuItem key={entry.value} onSelect={() => apply(entry.value)}>
          <span aria-hidden className="size-4 shrink-0 rounded-sm border border-border" style={{ backgroundColor: entry.value }} />
          {entry.name}{current === entry.value && <CheckIcon aria-hidden className="ml-auto" />}
        </DropdownMenuItem>)}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => setCustom(current ?? '#000000')}><PaletteIcon aria-hidden />Custom colour…</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <Dialog open={custom !== undefined} onOpenChange={open => { if (!open) setCustom(undefined); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>Custom text colour</DialogTitle>
          <DialogDescription>Applied to the selected text and kept in saved versions, Word and PDF exports. Choose a dark colour so the text stays readable.</DialogDescription></DialogHeader>
        <form className="space-y-4" onSubmit={event => { event.preventDefault(); if (custom && customValid) { apply(custom); setCustom(undefined); } }}>
          <TextColourField id="document-text-colour" value={custom ?? ''} disabled={unavailable} onChange={setCustom} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCustom(undefined)}>Cancel</Button>
            <Button type="submit" disabled={unavailable || !customValid}>Apply colour</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  </>;
}
